import React, { Suspense } from "react";
import { useAuth } from "./context/auth-context";
import Login from "./pages/Login";
import Register from "./pages/Register";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import ConfirmEmail from "./pages/ConfirmEmail";
import ResetPassword from "./pages/ResetPassword";
import Unsubscribe from "./pages/Unsubscribe";
import { adminRoutes, routes } from "./routes";
import { Switch, Route, Redirect, RouteProps } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { isSuperadmin, User } from "./auth-provider";
import Layout from "./components/Layout";
import { LoadingSpinner } from "./components/LoadingSpinner";
import { AppErrorBoundary } from "./components/AppErrorBoundary";
import "./i18n";
import "react-toastify/dist/ReactToastify.css";
import "./styles/index.css";
import { CurrentProjectProvider } from "./hooks/useProjects";
import { isBlankProjectParam } from "./utils/project-id";
import { ColorModeProvider } from "./context/color-mode-context";
import { DisplayFormatProvider, useDisplayFormat } from "./context/display-format-context";
import { ReactQueryDevtools } from "react-query-devtools";

interface ProtectedRoutes extends RouteProps {
  component: any;
  user: User | null;
}

function App() {
  const { user } = useAuth();

  return (
    <ColorModeProvider>
      <DisplayFormatProvider>
      <AppErrorBoundary>
        <Suspense fallback={<LoadingSpinner />}>
          <Switch>
            <Route exact path="/notifications/unsubscribe/" component={Unsubscribe} />
            <Route exact path="/register" component={Register} />
            <Route exact path="/privacy" component={PrivacyPolicy} />
            <Route exact path="/auth/confirm-email/" component={ConfirmEmail} />
            <Route
              path="/"
              render={() =>
                user ? (
                  isSuperadmin(user) ? (
                    <AdminApp />
                  ) : (
                    <CurrentProjectProvider>
                      <AuthenticatedApp />
                    </CurrentProjectProvider>
                  )
                ) : (
                  <UnauthenticatedApp />
                )
              }
            />
          </Switch>
        </Suspense>
      </AppErrorBoundary>
      {process.env.NODE_ENV === "development" && (
        <ReactQueryDevtools initialIsOpen={false} />
      )}
      </DisplayFormatProvider>
    </ColorModeProvider>
  );
}

// A password reset link is opened by a logged-out user, so the route has to be
// reachable without a session.
function UnauthenticatedApp() {
  return (
    <>
      <Switch>
        <Route exact path="/auth/reset-password/" component={ResetPassword} />
        <Route path="/" component={Login} />
      </Switch>
      <ToastContainer />
    </>
  );
}

function AdminApp() {
  const auth = useAuth();
  const { format } = useDisplayFormat();
  return (
    <React.Fragment key={format}>
      <Switch>
        <Layout type="admin">
          <Route exact path="/">
            <Redirect to="/users-management/" />
          </Route>
          {Object.values(adminRoutes).map((route) => {
            return (
              <PrivateRoute
                path={route.path}
                key={route.path}
                exact
                component={route.component}
                user={auth?.user}
              />
            );
          })}
        </Layout>
      </Switch>
      <ToastContainer />
    </React.Fragment>
  );
}

function AuthenticatedApp() {
  const { format } = useDisplayFormat();
  return (
    <React.Fragment key={format}>
      <Switch>
        <Layout>
          <Route exact path="/">
            <Redirect to="/dashboard" />
          </Route>
          <RenderRoutes />
        </Layout>
      </Switch>
      <ToastContainer />
    </React.Fragment>
  );
}

const RenderRoutes = () => {
  const auth = useAuth();
  return (
    <>
      {Object.values(routes).map((route) => {
        return (
          <PrivateRoute
            path={route.path}
            key={route.path}
            exact
            component={route.component}
            user={auth?.user}
          />
        );
      })}
    </>
  );
};

const PrivateRoute = ({
  user,
  component: Component,
  ...rest
}: ProtectedRoutes) => {
  return (
    <Route
      {...rest}
      render={(props) => {
        if (!user) {
          return (
            <Redirect
              to={{ pathname: `/login`, state: { from: props.location } }}
            />
          );
        }
        // /traffic-trend/%20/ and /content-decay/list/%20/ are not projects.
        // Rendering them requests the blank id and the module lists look empty.
        const projectId = (props.match.params as { projectId?: string }).projectId;
        if (isBlankProjectParam(projectId)) {
          return <Redirect to={routes.dashboard.path} />;
        }
        return <Component {...props} />;
      }}
    />
  );
};

export default App;
